export const OnboardingPhase = {
  Account: 'account',
  Profile: 'profile',
  Verify: 'verify',
} as const;
export type OnboardingPhase = (typeof OnboardingPhase)[keyof typeof OnboardingPhase];

export const OnboardingPhaseTitle = {
  Account: 'ACCOUNT',
  Profile: 'PROFILE',
  Verify: 'VERIFY',
} as const;
export type OnboardingPhaseTitle = (typeof OnboardingPhaseTitle)[keyof typeof OnboardingPhaseTitle];

export const OnboardingStep = {
  AccountName: 'accountName',
  AccountResidence: 'accountResidence',
  AccountBirth: 'accountBirth',
  AccountTax: 'accountTax',
  EconomicWealth: 'economicWealth',
  EconomicFunding: 'economicFunding',
  EconomicExperience: 'economicExperience',
  EconomicKnowledge: 'economicKnowledge',
  ProfilePep: 'profilePep',
  VerifyAddress: 'verifyAddress',
  VerifyIdentity: 'verifyIdentity',
  VerifyTerms: 'verifyTerms',
  VerifyDeclarations: 'verifyDeclarations',
} as const;
export type OnboardingStep = (typeof OnboardingStep)[keyof typeof OnboardingStep];

export const OnboardingStepTitle = {
  SignUp: 'SIGN UP LIVE ACCOUNT',
  AccountName: 'Personal details',
  AccountResidence: 'Residence & nationality',
  AccountBirth: 'Birth & contact',
  AccountTax: 'Tax details',
  EconomicWealth: 'Wealth & employment',
  EconomicFunding: 'Funding & objective',
  EconomicExperience: 'Investment experience',
  EconomicKnowledge: 'Decisions & knowledge',
  ProfilePep: 'Politically exposed person',
  VerifyAddress: 'Proof of address',
  VerifyIdentity: 'Proof of identity',
  VerifyTerms: 'Terms of service',
  VerifyDeclarations: 'Declarations',
  Done: 'Done',
} as const;
export type OnboardingStepTitle = (typeof OnboardingStepTitle)[keyof typeof OnboardingStepTitle];

export const ONBOARDING_ROOT_ROUTE = '/onboard';
export const ONBOARDING_RESULT_ROUTE = '/onboard/result';
export const LEGACY_SUPPORTING_DOCUMENTS_ROUTE = '/onboard/step5';

type Definition = {
  title: OnboardingStepTitle;
  path: string;
  route: string;
  currentStep: number;
  phase: OnboardingPhase;
  subStep: number;
  subTotal: number;
  previous?: OnboardingStep;
  next?: OnboardingStep;
};

const define = (
  title: OnboardingStepTitle,
  path: string,
  currentStep: number,
  phase: OnboardingPhase,
  subStep: number,
  subTotal: number,
  previous?: OnboardingStep,
  next?: OnboardingStep
): Definition => ({
  title,
  path,
  route: `${ONBOARDING_ROOT_ROUTE}/${path}`,
  currentStep,
  phase,
  subStep,
  subTotal,
  previous,
  next,
});

export const ONBOARDING_STEPS: Record<OnboardingStep, Definition> = {
  [OnboardingStep.AccountName]: define(
    OnboardingStepTitle.AccountName,
    'step1',
    1,
    OnboardingPhase.Account,
    1,
    4,
    undefined,
    OnboardingStep.AccountResidence
  ),
  [OnboardingStep.AccountResidence]: define(
    OnboardingStepTitle.AccountResidence,
    'step1-residence',
    1,
    OnboardingPhase.Account,
    2,
    4,
    OnboardingStep.AccountName,
    OnboardingStep.AccountBirth
  ),
  [OnboardingStep.AccountBirth]: define(
    OnboardingStepTitle.AccountBirth,
    'step1-birth',
    1,
    OnboardingPhase.Account,
    3,
    4,
    OnboardingStep.AccountResidence,
    OnboardingStep.AccountTax
  ),
  [OnboardingStep.AccountTax]: define(
    OnboardingStepTitle.AccountTax,
    'step1-tax',
    1,
    OnboardingPhase.Account,
    4,
    4,
    OnboardingStep.AccountBirth,
    OnboardingStep.EconomicWealth
  ),
  [OnboardingStep.EconomicWealth]: define(
    OnboardingStepTitle.EconomicWealth,
    'step2',
    2,
    OnboardingPhase.Profile,
    1,
    5,
    OnboardingStep.AccountTax,
    OnboardingStep.EconomicFunding
  ),
  [OnboardingStep.EconomicFunding]: define(
    OnboardingStepTitle.EconomicFunding,
    'step2-funding',
    2,
    OnboardingPhase.Profile,
    2,
    5,
    OnboardingStep.EconomicWealth,
    OnboardingStep.EconomicExperience
  ),
  [OnboardingStep.EconomicExperience]: define(
    OnboardingStepTitle.EconomicExperience,
    'step2-experience',
    2,
    OnboardingPhase.Profile,
    3,
    5,
    OnboardingStep.EconomicFunding,
    OnboardingStep.EconomicKnowledge
  ),
  [OnboardingStep.EconomicKnowledge]: define(
    OnboardingStepTitle.EconomicKnowledge,
    'step2-knowledge',
    2,
    OnboardingPhase.Profile,
    4,
    5,
    OnboardingStep.EconomicExperience,
    OnboardingStep.ProfilePep
  ),
  [OnboardingStep.ProfilePep]: define(
    OnboardingStepTitle.ProfilePep,
    'step3',
    3,
    OnboardingPhase.Profile,
    5,
    5,
    OnboardingStep.EconomicKnowledge,
    OnboardingStep.VerifyAddress
  ),
  [OnboardingStep.VerifyAddress]: define(
    OnboardingStepTitle.VerifyAddress,
    'step4',
    4,
    OnboardingPhase.Verify,
    1,
    4,
    OnboardingStep.ProfilePep,
    OnboardingStep.VerifyIdentity
  ),
  [OnboardingStep.VerifyIdentity]: define(
    OnboardingStepTitle.VerifyIdentity,
    'step4-identity',
    4,
    OnboardingPhase.Verify,
    2,
    4,
    OnboardingStep.VerifyAddress,
    OnboardingStep.VerifyTerms
  ),
  [OnboardingStep.VerifyTerms]: define(
    OnboardingStepTitle.VerifyTerms,
    'step6',
    5,
    OnboardingPhase.Verify,
    3,
    4,
    OnboardingStep.VerifyIdentity,
    OnboardingStep.VerifyDeclarations
  ),
  [OnboardingStep.VerifyDeclarations]: define(
    OnboardingStepTitle.VerifyDeclarations,
    'step6-declarations',
    5,
    OnboardingPhase.Verify,
    4,
    4,
    OnboardingStep.VerifyTerms
  ),
};

export const getOnboardingStepDefinition = (step: OnboardingStep) => ONBOARDING_STEPS[step];
export const getLegacySupportingDocumentsRedirect = (requestUrl: URL) =>
  `${getOnboardingStepDefinition(OnboardingStep.VerifyTerms).route}${requestUrl.search}`;
export const ONBOARDING_TOTAL_STEPS = Math.max(...Object.values(ONBOARDING_STEPS).map(step => step.currentStep));
export const getOnboardingRouteForLegacyStep = (legacyStep: number) =>
  Object.values(ONBOARDING_STEPS).find(step => step.currentStep === legacyStep)?.route;
export const getOnboardingRouteHandle = (step: OnboardingStep) => {
  const definition = getOnboardingStepDefinition(step);
  return {
    title: definition.title,
    currentStep: definition.currentStep,
    phase: definition.phase,
    subStep: definition.subStep,
    subTotal: definition.subTotal,
    prevRoute: definition.previous ? getOnboardingStepDefinition(definition.previous).route : undefined,
    nextRoute: definition.next ? getOnboardingStepDefinition(definition.next).route : ONBOARDING_RESULT_ROUTE,
  };
};
